import { Check, Terminal } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import { SlashCommandSharePayload } from './types';

interface SlashCommandImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  zIndex?: number;
  payload: SlashCommandSharePayload;
  onConfirmImport: (commands: SlashCommandSharePayload['commands']) => Promise<number>;
  onDismiss: () => void;
}

export const SlashCommandImportDialog = ({
  open,
  onOpenChange,
  zIndex,
  payload,
  onConfirmImport,
  onDismiss,
}: SlashCommandImportDialogProps) => {
  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(payload.commands.map((_, i) => i)),
  );

  const toggleItem = (index: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleImport = async () => {
    const selectedCommands = payload.commands.filter((_, i) => selected.has(i));
    if (selectedCommands.length === 0) {
      toast.error('No commands selected');
      return;
    }
    try {
      const count = await onConfirmImport(selectedCommands);
      if (count > 0) {
        toast.success(`Imported ${count} command${count > 1 ? 's' : ''}`);
      } else {
        toast.info('All selected commands already exist');
      }
    } catch {
      toast.error('Failed to import commands');
      return;
    }
    onDismiss();
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      onDismiss();
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" zIndex={zIndex}>
        <DialogHeader>
          <DialogTitle className="text-base font-medium text-gray-900">
            Import Slash Commands
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <p className="text-sm text-gray-600">
            Someone shared {payload.commands.length} command
            {payload.commands.length > 1 ? 's' : ''} with you. Select which
            ones to import.
          </p>

          <div className="flex flex-col border border-gray-200 rounded-md divide-y divide-gray-100 max-h-64 overflow-y-auto">
            {payload.commands.map((cmd, index) => (
              <button
                key={`${cmd.name}-${index}`}
                type="button"
                onClick={() => toggleItem(index)}
                className="flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
              >
                <div
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                    selected.has(index)
                      ? 'border-primary bg-primary'
                      : 'border-gray-200',
                  )}
                >
                  {selected.has(index) && (
                    <Check className="h-3 w-3 text-white" />
                  )}
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <Terminal className="h-3.5 w-3.5 text-gray-600 shrink-0" />
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium text-gray-900 font-mono leading-tight">
                      /{cmd.name}
                    </span>
                    {cmd.description && (
                      <span className="text-xs text-gray-600 truncate leading-snug">
                        {cmd.description}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          <p className="text-xs text-gray-600">
            Commands with duplicate names will be skipped.
          </p>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onDismiss}
              className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleImport}
              disabled={selected.size === 0}
              className="px-3 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Import {selected.size > 0 ? `(${selected.size})` : ''}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
